import React, { useEffect, useState } from 'react';
import axios from 'axios';

interface MentionedCycle {
  _id: string;
  /**
   * Where clicking should take the reader. For a mention on an embedded item
   * this is the PARENT cycle: the child has no tabs of its own, and the
   * question now renders in the parent's Consultations panel.
   */
  openCycleId?: string;
  /** Set on child rows only — the review the embedded item belongs to. */
  parentName?: string | null;
  contentItem: {
    name?: string;
    type?: string;
    audience?: string;
  };
  status: string;
  mentionCount: number;
  latestMention: {
    at: string;
    by: { _id: string; name: string; email: string };
    excerpt: string;
  } | null;
}

interface MentionedCyclesSectionProps {
  getAuthHeaders: () => Promise<Record<string, string>>;
  onOpenCycle?: (cycleId: string) => void;
}

const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:4000';
const STATUS_LABEL: Record<string, string> = {
  pending_prescreen: 'Awaiting Pre-Screen',
  pending_review: 'Pending Review',
  in_review: 'In Review',
  revision_needed: 'Revision Needed'
};

/**
 * Phase 12F — "Mentioned" section for any team member with active mentions.
 *
 * Fetched from GET /mentioned-cycles which filters to cycles still in-flight
 * (per D69 — terminal mentions stay in the audit but drop off the section).
 * Silent when the user has no active mentions.
 */
const MentionedCyclesSection: React.FC<MentionedCyclesSectionProps> = ({ getAuthHeaders, onOpenCycle }) => {
  const [rows, setRows] = useState<MentionedCycle[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function fetch() {
      try {
        const headers = await getAuthHeaders();
        const res = await axios.get<{ cycles: MentionedCycle[] }>(
          `${backendUrl}/api/content-review/mentioned-cycles`,
          { headers }
        );
        if (!cancelled) setRows(res.data.cycles);
      } catch (err: any) {
        if (!cancelled) setError(err.response?.data?.error || 'Failed to load mentions');
      }
    }
    fetch();
    return () => { cancelled = true; };
  }, [getAuthHeaders]);

  if (error) return null;
  if (!rows || rows.length === 0) return null;

  function formatTime(iso: string) {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleString('en-GB', {
      month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  }

  return (
    <div
      data-testid="mentioned-cycles-section"
      style={{
        marginBottom: 16, padding: 16, background: '#f0f7ff',
        borderRadius: 8, border: '1px solid #b8daff'
      }}
    >
      <h3 style={{ margin: '0 0 12px', fontSize: 16, color: '#123250' }}>
        Waiting on you <span style={{
          background: '#123250', color: '#fff', padding: '2px 8px', borderRadius: 12,
          fontSize: 12, marginLeft: 4
        }}>
          {rows.length}
        </span>
      </h3>
      {/* Renamed from "Mentioned" 2026-08-21: the list now also carries
          threads you STARTED that someone has answered — you are never
          mentioned in your own question, so the asker used to get an email and
          nothing here. A row appears while the next move is yours and clears
          when you reply or mark it answered. */}
      <p style={{ fontSize: 12, color: '#333', margin: '0 0 12px' }}>
        Threads where the next move is yours — someone tagged you, or answered a question
        you asked. A row clears when you reply or mark the thread answered, and comes back
        if they follow up. Being tagged also gives you read access to that review: you can
        open the content, reply and ask others for input, but not file findings or complete
        review steps.
      </p>
      <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
        {rows.map(row => (
          <li key={row._id} style={{
            padding: 12, background: '#fff', border: '1px solid #b8daff',
            borderRadius: 6, cursor: onOpenCycle ? 'pointer' : 'default'
          }}
            onClick={() => onOpenCycle?.(row.openCycleId || row._id)}
            onKeyDown={e => { if (onOpenCycle && (e.key === 'Enter' || e.key === ' ')) onOpenCycle(row.openCycleId || row._id); }}
            tabIndex={onOpenCycle ? 0 : -1}
            role={onOpenCycle ? 'button' : undefined}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#123250' }}>
                {row.contentItem.name || 'Untitled'}
                {/* A child item's own status never moves off Pre-Screen, so the
                    row reports the REVIEW's status; this says which review
                    that is (2026-08-21). */}
                {row.parentName && (
                  <div data-testid="mention-row-parent" style={{ fontSize: 11, fontWeight: 400, color: '#8a94a6', marginTop: 2 }}>
                    embedded in {row.parentName}
                  </div>
                )}
              </div>
              <div style={{ fontSize: 12, color: '#666', whiteSpace: 'nowrap' }}>
                {STATUS_LABEL[row.status] || row.status}
              </div>
            </div>
            {row.latestMention && (
              <div style={{ marginTop: 6, fontSize: 12, color: '#333' }}>
                by <strong>{row.latestMention.by.name}</strong> · {formatTime(row.latestMention.at)}
                {row.mentionCount > 1 && (
                  <span style={{ marginLeft: 6, color: '#666' }}>
                    ({row.mentionCount} threads)
                  </span>
                )}
              </div>
            )}
            {row.latestMention?.excerpt && (
              <div style={{
                marginTop: 6, padding: 6, background: '#f8f9fa', borderRadius: 4,
                fontSize: 12, color: '#333'
              }}>
                {row.latestMention.excerpt}
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
};

export default MentionedCyclesSection;
